"use client";

import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { CentralizedDrawer, Tooltip, TooltipContent, TooltipTrigger } from "@wtc/ui";

interface MessageDetailErrorProps {
  error: string;
  onClose: () => void;
  shouldUseDrawer?: boolean;
  className?: string;
}

export function MessageDetailError({
  error,
  onClose,
  shouldUseDrawer = false,
  className,
}: MessageDetailErrorProps) {
  const renderErrorContent = () => (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">Failed to load message details</p>
        <button type="button"
          onClick={onClose}
          className="px-4 py-2 bg-card-100 hover:bg-card-200 rounded-md text-sm"
        >
          Close
        </button>
      </div>
    </div>
  );

  if (shouldUseDrawer) {
    return (
      <CentralizedDrawer
        drawerId="mobile-message-detail"
        side="bottom"
        height="90vh"
        className={className}
        title="Error Loading Message"
        description={error || "Failed to load message"}
        showCloseButton={true}
        enableSwipeGesture={true}
      >
        <div className="flex-1 overflow-y-auto p-4">{renderErrorContent()}</div>
      </CentralizedDrawer>
    );
  }

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
            <svg
              className="h-5 w-5 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-red-600">Error Loading Message</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onClose}
              aria-label="Dismiss message load error"
              className="p-2 hover:bg-muted rounded-md"
            >
              <X className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Dismiss message load error</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex-1">{renderErrorContent()}</div>
    </div>
  );
}
